import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import EChart from "./EChart";
import { defaultGrid, tooltipCss } from "./chartDefaults";
import type { Timeseries } from "../../types.ts";
import { DimensionSeries } from "../../config/data.ts";
import { useControl } from "../../control/ControlContext.tsx";
import ChartFrame from "../ChartInspector";
import type { MeasureDef } from "../../config/metrics";
import type { AxisFormatter } from "./XmrChart";

interface CumBarChartProps {
  title: string;
  data: DimensionSeries;
  names?: string[];
  colors?: string[];
  height?: number;
  maxDimensions?: number;
  measure?: MeasureDef;
  axisFormatter?: AxisFormatter;
  contextLabel?: string;
  axisName?: string;
}

export function CumBarChart({
  title,
  data,
  colors,
  height = 400,
  maxDimensions = 10,
  measure,
  axisFormatter,
  contextLabel,
  axisName,
}: CumBarChartProps) {
  const { usePercent } = useControl();

  const { option, totalCategories, shownCategories, otherCount } = useMemo(
    () => buildOption(data, colors, height, maxDimensions, usePercent),
    [data, colors, height, maxDimensions, usePercent],
  );

  const chartLabel = usePercent ? "Dimension percent bars" : "Dimension stacked bars";

  // Build subtitle showing category breakdown
  const subtitle =
    otherCount > 0
      ? `Showing top ${shownCategories} of ${totalCategories} (${otherCount} in "Other")`
      : totalCategories > 1
        ? `Showing all ${totalCategories}`
        : undefined;

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      data={data}
      chartLabel={chartLabel}
      measure={measure}
      axisFormatter={axisFormatter}
      contextLabel={contextLabel}
    >
      <EChart option={applyAxisName(option, usePercent ? "%" : axisName)} height={height} />
    </ChartFrame>
  );
}

function applyAxisName(option: EChartsOption, axisName?: string): EChartsOption {
  if (!axisName) return option;
  return {
    ...option,
    yAxis: {
      ...(typeof option.yAxis === "object" && !Array.isArray(option.yAxis) ? option.yAxis : {}),
      name: axisName,
      nameLocation: "end",
      nameGap: 30,
    },
  } satisfies EChartsOption;
}

interface BuildOptionResult {
  option: EChartsOption;
  totalCategories: number;
  shownCategories: number;
  otherCount: number;
}

function buildOption(
  data: DimensionSeries,
  colors: string[] | undefined,
  height: number,
  maxDimensions: number,
  usePercent: boolean,
): BuildOptionResult {
  const { timestamps, series, palette, totalCategories, otherCount } = buildStackedSeries(
    Object.values(data),
    maxDimensions,
    Object.keys(data),
    colors,
    usePercent,
  );

  const shownCategories = otherCount > 0 ? series.length - 1 : series.length; // Don't count "Other" as a shown category

  const option: EChartsOption = {
    color: palette,
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      extraCssText: tooltipCss,
      valueFormatter: value => (usePercent ? `${value?.toFixed?.(2) ?? value}%` : `${Number(value).toFixed(2)}`),
    },
    legend: {
      type: "scroll",
      top: 0,
      left: "center",
    },
    grid: { ...defaultGrid, top: 70 },
    xAxis: {
      type: "time",
      boundaryGap: true,
    },
    yAxis: usePercent
      ? {
          type: "value",
          max: 100,
          axisLabel: { formatter: value => `${Number(value).toFixed(0)}%` },
        }
      : {
          type: "value",
          axisLabel: { formatter: value => formatCompact(Number(value)) },
        },
    series: series.map(seriesEntry => ({
      type: "bar",
      stack: "total",
      emphasis: { focus: "series" },
      name: seriesEntry.name,
      data: timestamps.map((ms, idx) => [ms, seriesEntry.values[idx]]),
    })),
  };

  return { option, totalCategories, shownCategories, otherCount };
}

interface StackedSeriesResult {
  timestamps: number[];
  series: { name: string; values: number[] }[];
  palette: string[];
  totalCategories: number;
  otherCount: number;
}

function buildStackedSeries(
  tss: Timeseries[],
  maxDimensions: number,
  names?: string[],
  colors?: string[],
  usePercent?: boolean,
): StackedSeriesResult {
  const tsSet = new Set<number>();
  for (const ts of tss) for (const d of ts.timestamps) tsSet.add(d.getTime());
  const sortedMs = Array.from(tsSet).sort((a, b) => a - b);

  const lookupTables = tss.map(ts => {
    const map = new Map<number, number>();
    for (let i = 0; i < ts.timestamps.length; i++) {
      map.set(ts.timestamps[i].getTime(), ts.data[i]);
    }
    return map;
  });

  const rawMatrix = lookupTables.map(map => sortedMs.map(ms => map.get(ms) ?? 0));

  // Calculate sums for all series, filter out zero-sum series, and sort by descending sum
  const seriesSums = rawMatrix
    .map((vals, idx) => ({ idx, sum: vals.reduce((acc, v) => acc + v, 0) }))
    .filter(s => s.sum !== 0); // Exclude categories with zero total
  seriesSums.sort((a, b) => b.sum - a.sum);

  const totalCategories = seriesSums.length; // Only count non-zero categories

  let finalMatrix: number[][];
  let finalNames: string[];
  let otherCount = 0;

  if (seriesSums.length > maxDimensions) {
    const topIndices = seriesSums.slice(0, maxDimensions).map(s => s.idx);
    const otherIndices = seriesSums.slice(maxDimensions).map(s => s.idx);
    otherCount = otherIndices.length;

    finalMatrix = topIndices.map(idx => rawMatrix[idx]);
    finalNames = topIndices.map(idx => names?.[idx] ?? `Series ${idx + 1}`);

    if (otherIndices.length > 0) {
      const otherData = sortedMs.map((_, timeIdx) =>
        otherIndices.reduce((sum, seriesIdx) => sum + rawMatrix[seriesIdx][timeIdx], 0),
      );
      finalMatrix.push(otherData);
      finalNames.push("Other");
    }
  } else {
    // Sort all series by sum descending (largest first = bottom of stack)
    const sortedIndices = seriesSums.map(s => s.idx);
    finalMatrix = sortedIndices.map(idx => rawMatrix[idx]);
    finalNames = sortedIndices.map(idx => names?.[idx] ?? `Series ${idx + 1}`);
  }

  // Move "Other" to the end (top of stack) if present
  if (otherCount > 0) {
    const otherIdx = finalNames.indexOf("Other");
    if (otherIdx !== -1 && otherIdx !== finalNames.length - 1) {
      const otherName = finalNames.splice(otherIdx, 1)[0];
      const otherData = finalMatrix.splice(otherIdx, 1)[0];
      finalNames.push(otherName);
      finalMatrix.push(otherData);
    }
  }

  const palette = colors && colors.length > 0 ? colors : defaultPalette();
  const normalizedMatrix = usePercent ? normalizeColumns(finalMatrix) : finalMatrix;

  const series = normalizedMatrix.map((values, idx) => ({
    name: finalNames[idx],
    values,
  }));

  return { timestamps: sortedMs, series, palette, totalCategories, otherCount };
}

function normalizeColumns(matrix: number[][]): number[][] {
  if (matrix.length === 0) return matrix;
  const columns = matrix[0].length;
  const normalized: number[][] = matrix.map(() => new Array(columns).fill(0));

  for (let col = 0; col < columns; col++) {
    const columnTotal = matrix.reduce((total, row) => total + row[col], 0);
    for (let row = 0; row < matrix.length; row++) {
      const base = matrix[row][col];
      normalized[row][col] = columnTotal === 0 ? 0 : (base / columnTotal) * 100;
    }
  }

  return normalized;
}

function defaultPalette(): string[] {
  return [
    "#D2C8FF", // violet
    "#26B941", // positive green
    "#EEFF53", // yellow
    "#97AFED", // blue
    "#BFBDA7", // olive
    "#F47979", // negative red
    "#927CFF", // deep violet
    "#6F7F72", // muted green
    "#40664C", // dark green
    "#BCB66D", // dark olive
    "#F23030", // strong red
  ];
}

function formatCompact(value: number, decimals = 1): string {
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(value / 1e12).toFixed(decimals)}t`;
  if (abs >= 1e9) return `${(value / 1e9).toFixed(decimals)}b`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(decimals)}m`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(decimals)}k`;
  return value.toFixed(Math.max(decimals, 0));
}

export default CumBarChart;
